// Video und Talkback zwischen den Teilnehmern (WebRTC), über den TURN-Server bei Hetzner.
// Audio für Aufnahmen läuft NICHT hierüber (verlustbehaftet), sondern als Take-Upload.
import type { SignalMsg } from '@srs/shared';
import { api, wsUrl } from '../api/client.ts';
import { useStudio } from '../state/store.ts';

type StreamListener = (userId: string, stream: MediaStream | null) => void;

export class Room {
  private ws: WebSocket | null = null;
  private pcs = new Map<string, RTCPeerConnection>();
  private ice: RTCIceServer[] = [];
  local: MediaStream | null = null;
  onStream: StreamListener = () => {};
  onRemoteRecord: (trackId: string, action: 'start' | 'stop', from: string) => void = () => {};

  constructor(public room: string) {}

  async start(media: MediaStreamConstraints = { video: true, audio: { echoCancellation: true } }) {
    const st = useStudio.getState();
    try {
      const t = await api.turn();
      this.ice = [{ urls: t.urls, username: t.username, credential: t.credential }];
    } catch { this.ice = [{ urls: 'stun:stun.l.google.com:19302' }]; }
    this.local = await navigator.mediaDevices.getUserMedia(media).catch(() => null);
    this.ws = new WebSocket(`${wsUrl('/signal')}?token=${encodeURIComponent(st.token ?? '')}`);
    this.ws.onopen = () => this.send({ type: 'join', room: this.room, userId: st.user.id });
    this.ws.onmessage = (e) => void this.handle(JSON.parse(String(e.data)) as SignalMsg);
  }

  private me() { return useStudio.getState().user.id; }
  private send(m: SignalMsg) { this.ws?.send(JSON.stringify(m)); }

  private peer(id: string) {
    let pc = this.pcs.get(id);
    if (pc) return pc;
    pc = new RTCPeerConnection({ iceServers: this.ice });
    this.local?.getTracks().forEach((t) => pc!.addTrack(t, this.local!));
    pc.onicecandidate = (e) => e.candidate && this.send({ type: 'ice', from: this.me(), to: id, candidate: e.candidate.toJSON() });
    pc.ontrack = (e) => this.onStream(id, e.streams[0]);
    pc.onconnectionstatechange = () => { if (pc!.connectionState === 'failed') this.drop(id); };
    this.pcs.set(id, pc);
    return pc;
  }

  private async handle(m: SignalMsg) {
    switch (m.type) {
      case 'peers':
        for (const id of m.peers) {
          const pc = this.peer(id);
          await pc.setLocalDescription(await pc.createOffer());
          this.send({ type: 'offer', from: this.me(), to: id, sdp: pc.localDescription!.sdp });
        }
        break;
      case 'offer': {
        const pc = this.peer(m.from);
        await pc.setRemoteDescription({ type: 'offer', sdp: m.sdp });
        await pc.setLocalDescription(await pc.createAnswer());
        this.send({ type: 'answer', from: this.me(), to: m.from, sdp: pc.localDescription!.sdp });
        break;
      }
      case 'answer': await this.pcs.get(m.from)?.setRemoteDescription({ type: 'answer', sdp: m.sdp }); break;
      case 'ice': await this.pcs.get(m.from)?.addIceCandidate(m.candidate as RTCIceCandidateInit).catch(() => undefined); break;
      case 'leave': this.drop(m.userId); break;
      case 'remoteRecord': this.onRemoteRecord(m.trackId, m.action, m.from); break;
    }
  }

  /** Produzent startet/stoppt die Aufnahme beim Remote-Writer (der muss es in seinen Einstellungen erlauben) */
  remoteRecord(to: string, trackId: string, action: 'start' | 'stop') { this.send({ type: 'remoteRecord', from: this.me(), to, trackId, action }); }

  private drop(id: string) { this.pcs.get(id)?.close(); this.pcs.delete(id); this.onStream(id, null); }

  stop() {
    this.pcs.forEach((pc) => pc.close()); this.pcs.clear();
    this.local?.getTracks().forEach((t) => t.stop());
    this.ws?.close();
  }
}

// ---- Gemeinsamer Zustand für Video-Kacheln (Camp und Session zeigen dieselben Streams) ----
type MediaState = { room: Room | null; streams: Record<string, MediaStream>; mic: boolean; cam: boolean };
let media: MediaState = { room: null, streams: {}, mic: true, cam: true };
const mediaSubs = new Set<() => void>();
const emit = (patch: Partial<MediaState>) => { media = { ...media, ...patch }; mediaSubs.forEach((s) => s()); };
export const mediaStore = {
  get: () => media,
  subscribe: (cb: () => void) => { mediaSubs.add(cb); return () => { mediaSubs.delete(cb); }; },
  async start(roomName: string) {
    if (media.room) return;
    const r = new Room(roomName);
    r.onStream = (id, s) => { const n = { ...media.streams }; if (s) n[id] = s; else delete n[id]; emit({ streams: n }); };
    emit({ room: r });
    await r.start();
    if (r.local) emit({ streams: { ...media.streams, [useStudio.getState().user.id]: r.local } });
  },
  stop() { media.room?.stop(); emit({ room: null, streams: {} }); },
  toggle(kind: 'mic' | 'cam') {
    const on = !media[kind];
    media.room?.local?.getTracks().filter((t) => t.kind === (kind === 'mic' ? 'audio' : 'video')).forEach((t) => { t.enabled = on; });
    emit({ [kind]: on } as Partial<MediaState>);
  },
};
